'use client';

import {
  useContext,
  useEffect,
  useState,
  type FC,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import {
  Button,
  ButtonGroup,
  Loading,
  Dialog,
  Tabs,
  Text,
  Toggle,
  Pill,
  Flex,
  Heading,
  Alert,
} from '@/_components';
import {
  generateQuestionnaireLink,
  saveQuestionnaire,
} from '@/_api/client-questionnaires';
import {
  markQuestionnaireCompleted,
  unsubmitQuestionnaire,
  sendDraftQuestionnaire,
} from '@/_api/admin-questionnaires';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { useCopy } from '@/_hooks/useCopy';
import { DocumentsPreviewTab } from './components/DocumentsPreviewTab';
import { SectionsPreviewTab } from './components/SectionsPreviewTab';
import { ActivitiesPreviewTab } from './components/ActivitiesPreviewTab';
import { UnattachedPreviewTab } from './components/UnattachedPreviewTab';
import { LockingConfirmationDialog } from './components/LockingConfirmationDialog';
import { RequestAdditionalItemsDialog } from './components/RequestAdditionalItemsDialog';
import { UnattachedPreviewDialog } from './components/UnattachedPreviewDialog';
import { usePathname, useRouter } from 'next/navigation';
import {
  getAdminDocumentDropOffPreviewLink,
  getAdminQuestionnaireViewLink,
} from '@/_utils/admin';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';
import { SELECTABLE_CLIENT_ROLES } from '@/_types/models/questionnaire';
import type {
  QuestionnairePreviewProps,
  QuestionnairePreviewTabs,
} from './QuestionnairePreview.types';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';

export const QuestionnairePreview: FC<QuestionnairePreviewProps> = ({
  isOpen,
  onClose,
  questionnaire,
  questionnaireCompany,
  isQuestionnaireLoading,
  isBulkUploadProcessing,
  isBUPReviewDialogOpenForSource,
  setLoading,
  reloadQuestionnairePreview,
  loadQuestionnaires,
  onExportToggle,
  setQuestionnaireExport,
  highlightedQuestionIds,
  useDialogLayout = true,
  setIsBulkUploadProcessing,
  setIsBUPReviewDialogOpenForSource,
  shouldAutoOpenBUPOnPreview,
  setShouldAutoOpenBUPOnPreview,
}) => {
  const [isLockedLoading, setLockedLoading] = useState<boolean>(false);
  const [currentTab, setCurrentTab] =
    useState<QuestionnairePreviewTabs>('sections');
  const [isRequestingAdditionalItems, setIsRequestingAdditionalItems] =
    useState<boolean>(false);
  const [isLockingQuestionnaire, setIsLockingQuestionnaire] =
    useState<boolean>(false);
  const [isMarkingCompleteDialog, setMarkingCompleteDialog] =
    useState<boolean>(false);
  const [isCompletedLoading, setCompletedLoading] = useState<boolean>(false);
  const [showUnattachedDialog, setShowUnattachedDialog] = useState(false);
  const [unattachedDocuments, setUnattachedDocuments] = useState<any[]>([]);
  const [depositStatusLoading, setDepositStatusLoading] =
    useState<boolean>(false);

  const { push } = useRouter();
  const pathname = usePathname();

  const { getDocumentsUploadedOutside } = useContext(PreviewContext);
  const { copyTextToClipboard } = useCopy();
  const { addToast } = useContext(ToastContext);
  const { token, permissions } = useContext(UserContext);
  const { setQuestionnairePreview } = useContext(QuestionnaireContext);

  const hasEditQuestionnairePermission: boolean = getHasPermission(
    PermissionsMapEnum['questionnaire.edit'],
    permissions,
  );

  const resetState = useCallback((): void => {
    setIsRequestingAdditionalItems(false);
    setIsLockingQuestionnaire(false);
    setCurrentTab('sections');
  }, []);

  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }

    return () => {
      resetState();
    };
  }, [isOpen, resetState]);

  const onUpdateTrackChanges = async () => {
    if (!questionnaire) return;

    const trackChanges = !questionnaire.track_changes;

    try {
      setQuestionnairePreview({
        ...questionnaire,
        track_changes: trackChanges,
      });

      const { error } = await saveQuestionnaire({
        companyId: questionnaire.company_id!,
        id: questionnaire.id!,
        params: { track_changes: trackChanges },
        token,
      });

      if (error) {
        addToast('error', 'Failed to update track changes');
        setQuestionnairePreview({
          ...questionnaire,
          track_changes: !trackChanges,
        });
      }
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const markDepositStatus = async (status: 'marked_paid' | 'pending') => {
    if (!questionnaire) return;

    setDepositStatusLoading(true);

    try {
      await saveQuestionnaire({
        companyId: questionnaire.company_id!,
        id: questionnaire.id!,
        params: { deposit_status: status },
        token,
      });

      await reloadQuestionnairePreview();
      await loadQuestionnaires(undefined, true);

      addToast(
        'success',
        `Questionnaire marked as ${status === 'marked_paid' ? 'paid' : 'unpaid'}`,
      );
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setDepositStatusLoading(false);
    }
  };

  const onCopyLink = async () => {
    if (!questionnaire) return;

    try {
      const { link } = (await generateQuestionnaireLink({
        companyId: questionnaire.company_id!,
        questionnaireId: questionnaire.id!,
        token,
      })) as any;

      copyTextToClipboard(link);
      addToast('success', 'Link copied to clipboard');
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const onUpdateLocked = async (locked: boolean) => {
    if (!questionnaire) return;

    setLockedLoading(true);

    try {
      await saveQuestionnaire({
        companyId: questionnaire.company_id!,
        id: questionnaire.id!,
        params: { locked },
        token,
      });

      await reloadQuestionnairePreview();
      await loadQuestionnaires(undefined, true);

      const message = locked
        ? 'Questionnaire locked'
        : 'Questionnaire unlocked';

      addToast('success', message);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLockedLoading(false);
      setIsLockingQuestionnaire(false);
    }
  };

  const onConfirmLocked = () => {
    onUpdateLocked(true);
  };

  const onToggleLocked = () => {
    if (!questionnaire) return;

    try {
      questionnaire.locked
        ? onUpdateLocked(false)
        : setIsLockingQuestionnaire(true);
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const onPreviewQuestionnaire = () => {
    if (!questionnaire) return;

    let previewLink: string | undefined;
    if (questionnaire.document_drop_off_enabled) {
      previewLink = getAdminDocumentDropOffPreviewLink(
        questionnaire.id!,
        questionnaire.company_id!,
      );
    } else {
      previewLink = getAdminQuestionnaireViewLink(
        questionnaire.id!,
        questionnaire.company_id!,
        false,
      );
    }

    onClose();

    if (!previewLink) {
      addToast('error', 'No preview link found');
      return;
    }

    push(previewLink);
  };

  const onSendToClient = async (): Promise<void> => {
    if (!questionnaire) return;

    try {
      setLoading(true);
      await sendDraftQuestionnaire({
        questionnaire_ids: [questionnaire.id!],
        token,
      });

      await reloadQuestionnairePreview();
      await loadQuestionnaires(undefined, true);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  };

  const onToggleCompleteDialog = () =>
    setMarkingCompleteDialog((previous) => !previous);

  const onCompleteStatus = async () => {
    if (!questionnaire) return;

    setCompletedLoading(true);

    try {
      const params = {
        companyId: questionnaire.company_id!,
        id: questionnaire.id!,
        token,
      };

      if (questionnaire.completed) {
        await unsubmitQuestionnaire(params);
        addToast('success', 'Questionnaire marked not completed');
      } else {
        const { error } = await markQuestionnaireCompleted(params);

        if (error) {
          addToast('error', error);
        } else {
          addToast('success', 'Questionnaire marked completed');
        }
      }

      await reloadQuestionnairePreview();
      await loadQuestionnaires(undefined, true);
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setMarkingCompleteDialog(false);
      setCompletedLoading(false);
    }
  };

  const handleExportClick = async () => {
    if (!questionnaire) return;

    try {
      const response = await getDocumentsUploadedOutside(
        questionnaire.company_id!,
        true,
      );
      if (!response.error) {
        const documents = response as unknown as any[];
        setUnattachedDocuments(documents ?? []);

        if (documents?.length > 0) {
          setShowUnattachedDialog(true);
        } else {
          onExportQuestionnaire();
        }
      } else {
        onExportQuestionnaire();
      }
    } catch (error) {
      onExportQuestionnaire();
      console.error('error pulling unattached documents', error);
    }
  };

  const onExportQuestionnaire = () => {
    setShowUnattachedDialog(false);
    if (questionnaire) {
      setQuestionnaireExport([questionnaire]);
    }
    onExportToggle();
  };

  // Auto-open BUP Review when coming from Overview Documents cell
  useEffect(() => {
    if (
      isOpen &&
      shouldAutoOpenBUPOnPreview &&
      questionnaire &&
      currentTab !== 'documents'
    ) {
      setCurrentTab('documents');
    }
  }, [isOpen, shouldAutoOpenBUPOnPreview, questionnaire, currentTab]);

  useEffect(() => {
    if (
      isOpen &&
      shouldAutoOpenBUPOnPreview &&
      questionnaire &&
      currentTab === 'documents'
    ) {
      // Find the first bulk document that needs review (not completed)
      const firstBulk = questionnaire?.extracted_documents?.find(
        (d: any) =>
          d?.document_type === 'bulk_document' &&
          typeof d?.source === 'string' &&
          d?.source.toLowerCase().endsWith('.pdf') &&
          d?.status !== 'split_succeeded', // Skip completed documents
      );

      if (firstBulk?.source) {
        setIsBUPReviewDialogOpenForSource(firstBulk.source);
        setShouldAutoOpenBUPOnPreview(false);
      } else if (
        Array.isArray(questionnaire?.extracted_documents) &&
        questionnaire?.extracted_documents.length > 0
      ) {
        // Prevent repeated attempts if no qualifying bulk document found
        setShouldAutoOpenBUPOnPreview(false);
      }
    }
  }, [
    isOpen,
    shouldAutoOpenBUPOnPreview,
    questionnaire,
    questionnaire?.extracted_documents,
    currentTab,
    setIsBUPReviewDialogOpenForSource,
    setShouldAutoOpenBUPOnPreview,
  ]);

  const isMarkPaidAvailable: boolean = useMemo(
    () =>
      !!questionnaire?.deposit_amount &&
      (questionnaire?.deposit_status === 'pending' ||
        questionnaire?.deposit_status === 'unpaid'),
    [questionnaire?.deposit_amount, questionnaire?.deposit_status],
  );

  const matchedCustomReceiverClientRole: {
    value: string;
    name: string;
  } | null = useMemo(() => {
    return (
      SELECTABLE_CLIENT_ROLES.find(
        (role) => role.value === questionnaire?.custom_receiver_client_role,
      ) || null
    );
  }, [questionnaire?.custom_receiver_client_role]);

  const title = questionnaire?.name || 'Loading...';
  const subtitle = questionnaireCompany?.name || 'Loading...';

  const headerButtons: ReactNode = (
    <Flex gap={2} isRow={true} isYCentered={true}>
      {isQuestionnaireLoading && !isBulkUploadProcessing && (
        <Loading size={16} />
      )}
      {!questionnaire?.is_draft && (
        <Button
          intent="ghost"
          size="small"
          iconRight="clipboard"
          isIconOnly={true}
          withTooltip
          tooltipPlacement="bottom"
          onClick={onCopyLink}
          value="Copy Link"
        />
      )}
      <Button
        intent="ghost"
        isActive={!!questionnaire?.locked}
        size="small"
        iconRight="eye"
        isIconOnly={true}
        withTooltip
        tooltipPlacement="bottom"
        onClick={onPreviewQuestionnaire}
        value="Preview"
        isDisabled={!questionnaire}
      />
      <Button
        intent="ghost"
        isActive={!!questionnaire?.locked}
        size="small"
        iconRight="lock"
        isLoading={isLockedLoading}
        isIconOnly={true}
        withTooltip
        tooltipPlacement="bottom"
        isDisabled={isLockedLoading || !questionnaire}
        onClick={onToggleLocked}
        value={questionnaire?.locked ? 'Unlock' : 'Lock'}
      />
      <Button
        intent="ghost"
        size="small"
        iconRight="plus-circle"
        isIconOnly={true}
        withTooltip
        tooltipPlacement="bottom"
        isDisabled={
          !questionnaire ||
          questionnaire.locked ||
          !hasEditQuestionnairePermission
        }
        onClick={() => setIsRequestingAdditionalItems(true)}
        value="Request additional items"
      />
      <Button
        intent="ghost"
        size="small"
        iconRight="download"
        isIconOnly={true}
        withTooltip
        tooltipPlacement="bottom"
        onClick={handleExportClick}
        value="Export"
        isDisabled={!questionnaire}
      />
    </Flex>
  );

  const customRecipientPill: ReactNode =
    questionnaire?.custom_receiver_client_role ? (
      <Text type="small" fontWeight="bold">
        Custom Recipient{' '}
        <Pill
          intent="info"
          label={matchedCustomReceiverClientRole?.name || ''}
        />
      </Text>
    ) : null;

  const tabsContent = (
    <Tabs
      noPadding={true}
      items={[
        {
          title: 'Sections',
          isActive: currentTab === 'sections',
          onClick: () => setCurrentTab('sections'),
        },
        {
          title: 'Documents',
          isActive: currentTab === 'documents',
          onClick: () => setCurrentTab('documents'),
        },
        {
          title: 'Activities',
          isActive: currentTab === 'activities',
          onClick: () => setCurrentTab('activities'),
        },
        {
          title: 'Unattached',
          isActive: currentTab === 'unattached',
          onClick: () => setCurrentTab('unattached'),
        },
      ]}
    >
      {questionnaire && currentTab === 'sections' && (
        <SectionsPreviewTab
          questionnaire={questionnaire}
          highlightedQuestionIds={highlightedQuestionIds}
          reloadQuestionnairePreview={reloadQuestionnairePreview}
          isLoadingQuestionnairePreview={isQuestionnaireLoading}
        />
      )}

      {questionnaire && currentTab === 'documents' && (
        <DocumentsPreviewTab
          questionnaire={questionnaire}
          isLoadingQuestionnairePreview={isQuestionnaireLoading}
          reloadQuestionnairePreview={reloadQuestionnairePreview}
          isBulkUploadProcessing={isBulkUploadProcessing}
          setIsBulkUploadProcessing={setIsBulkUploadProcessing}
          isBUPReviewDialogOpenForSource={isBUPReviewDialogOpenForSource}
          setIsBUPReviewDialogOpenForSource={setIsBUPReviewDialogOpenForSource}
        />
      )}

      {questionnaire && currentTab === 'activities' && (
        <ActivitiesPreviewTab questionnaire={questionnaire} />
      )}

      {questionnaire && currentTab === 'unattached' && (
        <UnattachedPreviewTab
          questionnaire={questionnaire}
          reloadQuestionnairePreview={reloadQuestionnairePreview}
        />
      )}
    </Tabs>
  );

  const footerButtons: ReactNode = (
    <Flex isBetween={true}>
      <ButtonGroup align="left">
        {questionnaire?.is_draft && (
          <Pill
            intent="info"
            isRound={true}
            size="large"
            label="Draft"
            iconLeft="info"
          />
        )}
      </ButtonGroup>
      <ButtonGroup align="right">
        {questionnaire?.deposit_status === 'paid' && (
          <Pill intent="success" label="Deposit Paid" iconLeft="check-circle" />
        )}
        {questionnaire?.deposit_status === 'marked_paid' && (
          <Pill
            intent="success"
            label="Deposit Marked Paid"
            iconLeft="check-circle"
          />
        )}
        {questionnaire?.deposit_status === 'payment_pending' && (
          <Pill intent="info" label="Deposit Pending" iconLeft="spinner-gap" />
        )}
        {isMarkPaidAvailable && (
          <Pill intent="danger" label="Deposit Unpaid" iconLeft="x-circle" />
        )}
        {isMarkPaidAvailable && (
          <Button
            intent="secondary"
            onClick={() => markDepositStatus('marked_paid')}
            isLoading={depositStatusLoading}
            isDisabled={depositStatusLoading}
            value="Mark as paid"
          />
        )}
        {questionnaire?.deposit_status === 'marked_paid' && (
          <Button
            intent="secondary"
            onClick={() => markDepositStatus('pending')}
            isLoading={depositStatusLoading}
            isDisabled={depositStatusLoading}
            value="Mark as unpaid"
          />
        )}
        <Toggle
          label="Track Changes"
          isReverse={true}
          isDisabled={
            isQuestionnaireLoading ||
            !hasEditQuestionnairePermission ||
            !questionnaire
          }
          isChecked={!!questionnaire?.track_changes}
          onChange={onUpdateTrackChanges}
        />
        <Button
          intent="secondary"
          onClick={onToggleCompleteDialog}
          isLoading={isCompletedLoading}
          isDisabled={
            isCompletedLoading ||
            isQuestionnaireLoading ||
            !hasEditQuestionnairePermission
          }
          value={
            questionnaire?.completed ? 'Mark incomplete' : 'Mark completed'
          }
        />
        {questionnaire?.is_draft && (
          <Button
            value="Send to user"
            isLoading={isQuestionnaireLoading}
            isDisabled={isQuestionnaireLoading}
            onClick={onSendToClient}
            intent="primary"
          />
        )}
      </ButtonGroup>
    </Flex>
  );

  const bodyContent = (
    <>
      {customRecipientPill}
      {tabsContent}
    </>
  );

  const modalStack: ReactNode = (
    <>
      <Dialog
        title={
          questionnaire?.completed
            ? 'Mark questionnaire incomplete'
            : 'Mark questionnaire completed'
        }
        isOpen={isMarkingCompleteDialog}
        onClose={onToggleCompleteDialog}
      >
        <Dialog.Content>
          <Flex isColumn={true} gap={8}>
            <Text type="paragraph">
              Are you sure you want to mark{' '}
              <b>{questionnaire?.formatted_name || 'this Questionnaire'}</b>{' '}
              {questionnaire?.completed ? 'incomplete' : 'completed'}?
            </Text>
            {questionnaire?.is_draft && (
              <Alert type="warning">
                This is still a draft questionnaire, it will not be visible to
                the client.
              </Alert>
            )}
          </Flex>
        </Dialog.Content>
        <Dialog.Footer>
          <ButtonGroup align="right">
            <Button
              value="Cancel"
              intent="secondary"
              onClick={onToggleCompleteDialog}
              isDisabled={isCompletedLoading}
            />
            <Button
              value={
                questionnaire?.completed ? 'Mark incomplete' : 'Mark completed'
              }
              intent={questionnaire?.completed ? 'danger' : 'success'}
              onClick={onCompleteStatus}
              isDisabled={isCompletedLoading}
              isLoading={isCompletedLoading}
            />
          </ButtonGroup>
        </Dialog.Footer>
      </Dialog>
      <LockingConfirmationDialog
        isOpen={isLockingQuestionnaire}
        isLoading={isLockedLoading}
        onConfirm={onConfirmLocked}
        onClose={() => setIsLockingQuestionnaire(false)}
      />
      <RequestAdditionalItemsDialog
        questionnaire={questionnaire}
        reloadQuestionnairePreview={reloadQuestionnairePreview}
        isOpen={isRequestingAdditionalItems}
        onClose={() => setIsRequestingAdditionalItems(false)}
      />
      {questionnaire && (
        <UnattachedPreviewDialog
          questionnaire={questionnaire}
          reloadQuestionnairePreview={reloadQuestionnairePreview}
          isOpen={showUnattachedDialog}
          onClose={() => setShowUnattachedDialog(false)}
          onExportToggle={onExportQuestionnaire}
          initialDocuments={unattachedDocuments}
        />
      )}
    </>
  );

  if (!useDialogLayout) {
    return (
      <>
        <Flex isColumn={true} gap={8} isFullWidth={true}>
          <Flex isBetween={true} isYCentered={true}>
            <Flex isColumn={true} gap={1}>
              <Heading level={3}>{title}</Heading>
              <Text type="small" color="secondary">
                {subtitle}
              </Text>
            </Flex>
            {headerButtons}
          </Flex>
          {bodyContent}
          {footerButtons}
        </Flex>
        {modalStack}
      </>
    );
  }

  return (
    <>
      <Dialog
        isOpen={isOpen}
        isPersistent={!!isBUPReviewDialogOpenForSource}
        isWide={true}
        isTall={true}
        onClose={onClose}
        title={title}
        titleTag={subtitle}
        headerButtons={headerButtons}
      >
        <Dialog.Content>{bodyContent}</Dialog.Content>
        <Dialog.Footer>{footerButtons}</Dialog.Footer>
      </Dialog>
      {modalStack}
    </>
  );
};
